import { Button, Tooltip, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import {
  HomeOutlined,
  ProjectOutlined,
  SettingOutlined,
  BarChartOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DeploymentUnitOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/context/AuthContext';
import { useTenantContext } from '@/context/TenantContext';
import { api } from '@/services/api';
import type { Tenant } from '@/types/tenant';
import LogoBrand from '@/components/common/LogoBrand';

interface SidebarProps {
  mobile?: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({ mobile = false, onNavigate, collapsed = false, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname === '/' ? '/home' : location.pathname;
  const { user, hasPermission, hasFeature } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const isAuthenticated = !!user;

  // Estado para armazenar o logo do tenant
  const [tenantLogo, setTenantLogo] = useState<string | null>(null);

  // Carregar logo do tenant quando selectedTenantId mudar
  useEffect(() => {
    const loadTenantLogo = async () => {
      // Só carregar se estiver autenticado
      if (!isAuthenticated) {
        setTenantLogo(null);
        return;
      }

      // SuperAdmin sem tenant específico: não carrega logo
      if (user?.is_superadmin && !user?.tenant_id) {
        setTenantLogo(null);
        return;
      }

      try {
        // Usar endpoint /tenants/current que não exige superadmin
        const response = await api.get<Tenant>('/tenants/current');
        
        if (response?.config?.logo_url) {
          setTenantLogo(response.config.logo_url);
        } else {
          setTenantLogo(null);
        }
      } catch (error) {
        // Silenciosamente falhar - logo é opcional
        console.error('[Sidebar] Error loading tenant logo:', error);
        setTenantLogo(null);
      }
    };

    loadTenantLogo();
  }, [selectedTenantId, isAuthenticated]);

  // Permission gates aligned to matrix:
  // - read/edit_read → Projetos only
  // - edit_read_delete → + Home, Relatórios, OKR Dashboard, Cadastros, Bridges
  // - admin → + Administração
  const canAccessReports = hasPermission('edit_read_delete');
  const canAccessCadastros = hasPermission('edit_read_delete');
  const canAccessBridges = hasPermission('edit_read_delete');
  const canAccessAdmin = hasPermission('admin');
  const canViewOkrDashboard = hasPermission('edit_read_delete');

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    const requiredKeys: string[] = [];
    
    // Expandir "Relatórios" se estiver em rotas relacionadas
    if ((canAccessReports || canViewOkrDashboard) && 
        (currentPath.startsWith('/relatorios') || currentPath.startsWith('/okrs/dashboard'))) {
      requiredKeys.push('relatorios');
    }
    
    // Expandir "Cadastros" se estiver em rotas relacionadas
    if (canAccessCadastros && 
        (currentPath.startsWith('/cadastros') || currentPath.startsWith('/app/committees'))) {
      requiredKeys.push('cadastros');
    }
    
    // Expandir "Bridges" se estiver em rotas relacionadas
    if (canAccessBridges && (currentPath.startsWith('/bridges') || currentPath.startsWith('/app/intake'))) {
      requiredKeys.push('bridges');
    }

    setOpenKeys((prev) => {
      const enforcedSet = new Set(requiredKeys);
      const allowedPrevious = prev.filter((key) => {
        // Apenas categorias válidas no sidebar: relatorios, cadastros, bridges
        if (!['relatorios', 'cadastros', 'bridges'].includes(key)) {
          return true;
        }
        return enforcedSet.has(key);
      });
      const merged = new Set([...allowedPrevious, ...requiredKeys]);
      return Array.from(merged);
    });
  }, [currentPath, canAccessReports, canViewOkrDashboard, canAccessCadastros, canAccessBridges]);
  const selectedKey = useMemo(() => {
  if (currentPath.startsWith('/cadastros/')) return currentPath;
  if (currentPath.startsWith('/okrs/dashboard')) return '/okrs/dashboard';
  if (currentPath.startsWith('/app/intake')) return '/app/intake';
  if (currentPath.startsWith('/app/committees')) return '/app/committees';
  if (currentPath.startsWith('/app/committee/approvals')) return '/app/committee/approvals';
    if (currentPath.startsWith('/bridges/1')) return '/bridges/1';
    if (currentPath.startsWith('/bridges/2')) return '/bridges/2';
    if (currentPath.startsWith('/bridges/3')) return '/bridges/3';
    if (currentPath.startsWith('/bridges/4')) return '/bridges/4';
    if (currentPath.startsWith('/projeto')) return '/projetos';
    if (currentPath.startsWith('/projetos')) return '/projetos';
    if (currentPath.startsWith('/relatorios')) return '/relatorios';
    if (currentPath.startsWith('/home')) return '/home';
    return '/projetos';
  }, [currentPath]);

  const menuItems = useMemo<MenuProps['items']>(() => {
    if (!isAuthenticated) {
      return [];
    }

    const canAccessHome = hasPermission('edit_read_delete');

    const items: MenuProps['items'] = [
      ...(canAccessHome ? [{ key: '/home', icon: <HomeOutlined />, label: <span data-tour="sidebar-home">Home</span> }] : []),
      { key: '/projetos', icon: <ProjectOutlined />, label: <span data-tour="sidebar-projetos">Projetos</span> },
    ];

    // Ordem alfabética após Home e Projetos: Bridges, Cadastros, Relatórios
    if (canAccessBridges && hasFeature('bridges')) {
      items.push({
        key: 'bridges',
        icon: <DeploymentUnitOutlined />,
        label: <span data-tour="sidebar-bridges">Gates</span>,
        children: [
          { key: '/app/intake', label: 'Intake (Gate 0)' },
          { key: '/pmo/pipeline', label: 'Pipeline do Portfólio' },
        ],
      });
    }

    if (canAccessCadastros) {
      const cadastrosChildren = [
        { key: '/cadastros/responsaveis', label: 'Responsáveis' },
        ...(hasFeature('cost_management') ? [{ key: '/cadastros/grupos-custo', label: 'Categoria Econômica' }] : []),
        ...(hasFeature('programs') ? [{ key: '/cadastros/programas', label: 'Programas' }] : []),
        ...(hasFeature('okrs') ? [{ key: '/cadastros/okrs', label: 'OKRs' }] : []),
        ...(hasFeature('committees') ? [{ key: '/app/committees', label: 'Comitês' }] : []),
      ];
      items.push({
        key: 'cadastros',
        icon: <SettingOutlined />,
        label: <span data-tour="sidebar-cadastros">Cadastros</span>,
        children: cadastrosChildren,
      });
    }

    // Categoria "Relatórios" colapsável (agrupa Visão Executiva + Dashboards OKR)
    if (canAccessReports || canViewOkrDashboard) {
      const relatoriosChildren = [];

      if (canAccessReports && hasFeature('dashboard_executive')) {
        relatoriosChildren.push({ key: '/relatorios', label: 'Visão Executiva' });
        relatoriosChildren.push({
          key: '/relatorios/portfolio-ia',
          label: <span data-tour="portfolio-ai-sidebar">Relatório IA — Portfólio</span>,
        });
      }

      if (canViewOkrDashboard && hasFeature('okrs')) {
        relatoriosChildren.push({ key: '/okrs/dashboard', label: 'Dashboards OKR' });
      }

      if (relatoriosChildren.length > 0) {
        items.push({
          key: 'relatorios',
          icon: <BarChartOutlined />,
          label: <span data-tour="sidebar-relatorios">Relatórios</span>,
          children: relatoriosChildren,
        });
      }
    }

    return items;
  }, [isAuthenticated, canAccessBridges, canAccessCadastros, canAccessReports, canViewOkrDashboard, canAccessAdmin, hasFeature]);

  const handleMenuClick: MenuProps['onClick'] = (info) => {
    if (typeof info.key === 'string' && info.key.startsWith('/')) {
      navigate(info.key);
      if (mobile && onNavigate) onNavigate();
    }
  };

  return (
    <div
      style={{
        padding: mobile ? '16px' : '24px 12px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flex: 1,
        overflowY: mobile ? 'visible' : 'auto',
      }}
    >
      {!mobile && (
        <div style={{ 
          display: 'flex', 
          justifyContent: collapsed ? 'center' : 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          paddingTop: 8,
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          {/* Quando expandido: espaçador invisível à esquerda para balancear */}
          {!collapsed && <div style={{ width: 28, flexShrink: 0, transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }} />}
          
          {/* Tenant Logo ou Portal PMO fallback - centralizado (oculto quando colapsado) */}
          {!collapsed && (
            <div style={{ 
              flex: 1,
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              opacity: 1,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
              {tenantLogo ? (
                <img 
                  src={tenantLogo} 
                  alt="Tenant Logo"
                  style={{
                    maxWidth: '80%',
                    maxHeight: '60px',
                    minHeight: '40px',
                    objectFit: 'contain',                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',                }}
                  onError={(e) => {
                    console.error('Error loading logo image:', e);
                    setTenantLogo(null);
                  }}
                />
              ) : (
                <LogoBrand size={20} />
              )}
            </div>
          )}
          
          {/* Botão de collapse - centralizado quando colapsado, à direita quando expandido */}
          <Tooltip title={collapsed ? 'Expandir menu' : 'Recolher menu'} placement="right">
            <Button
              type="text"
              size="small"
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
              onClick={onToggleCollapse}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              style={{ 
                borderRadius: 6, 
                width: 28, 
                height: 28, 
                flexShrink: 0,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </Tooltip>
        </div>
      )}

      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={collapsed ? [] : openKeys}
        onOpenChange={(keys) => setOpenKeys(keys as string[])}
        onClick={handleMenuClick}
        items={menuItems}
        inlineCollapsed={collapsed}
        style={{ borderInline: 'none', flex: 1, background: 'transparent' }}
      />
    </div>
  );
}
